import { type FC, type ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './DashboardLayout.css';

interface DashboardLayoutProps {
  children: ReactNode;
}

export const DashboardLayout: FC<DashboardLayoutProps> = ({ children }) => {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const pageTitle = (): string => {
    const p = location.pathname;
    if (p === '/dashboard') return 'Overview';
    if (p.startsWith('/dashboard/users/')) return 'User detail';
    if (p === '/dashboard/users') return 'User management';
    if (p.startsWith('/dashboard/providers/')) return 'Provider detail';
    if (p === '/dashboard/providers') return 'Provider management';
    if (p.startsWith('/dashboard/bookings/')) return 'Booking detail';
    if (p === '/dashboard/bookings') return 'Booking management';
    if (p === '/dashboard/promotions') return 'Promotions';
    if (p === '/dashboard/support-tickets') return 'Support tickets';
    if (p === '/dashboard/customer-ads') return 'Customer ads';
    if (p === '/dashboard/feedback') return 'Feedback';
    if (p === '/dashboard/incident-reports') return 'Incident reports';
    if (p === '/dashboard/reviews') return 'Reviews';
    if (p === '/dashboard/notifications') return 'Notifications';
    if (p === '/dashboard/analytics') return 'Analytics';
    if (p === '/dashboard/reports') return 'Reports';
    if (p === '/dashboard/system-settings') return 'System settings';
    return 'Admin';
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={`dashboard-container${sidebarOpen ? ' dashboard-container--menu-open' : ''}`}>
      <button
        type="button"
        className={`mobile-menu-toggle ${sidebarOpen ? 'mobile-menu-toggle--open' : ''}`}
        onClick={toggleSidebar}
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={sidebarOpen}
      >
        {sidebarOpen ? '✕' : '☰'}
      </button>
      <div className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={closeSidebar}></div>
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Gramio</h2>
          <p className="sidebar-subtitle">Admin Dashboard</p>
        </div>

        <nav className="sidebar-nav">
          <Link
            to="/dashboard"
            className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
          >
            Dashboard
          </Link>
          <Link
            to="/dashboard/users"
            className={`nav-link ${isActive('/dashboard/users') ? 'active' : ''}`}
          >
            User Management
          </Link>
          <Link
            to="/dashboard/providers"
            className={`nav-link ${isActive('/dashboard/providers') ? 'active' : ''}`}
          >
            Provider Management
          </Link>
          <Link
            to="/dashboard/bookings"
            className={`nav-link ${isActive('/dashboard/bookings') ? 'active' : ''}`}
          >
            Booking Management
          </Link>
          <Link
            to="/dashboard/promotions"
            className={`nav-link ${isActive('/dashboard/promotions') ? 'active' : ''}`}
          >
            Promotions
          </Link>
          <Link
            to="/dashboard/support-tickets"
            className={`nav-link ${isActive('/dashboard/support-tickets') ? 'active' : ''}`}
          >
            Support Tickets
          </Link>
          <Link
            to="/dashboard/customer-ads"
            className={`nav-link ${isActive('/dashboard/customer-ads') ? 'active' : ''}`}
          >
            Customer ads
          </Link>
          <Link
            to="/dashboard/feedback"
            className={`nav-link ${isActive('/dashboard/feedback') ? 'active' : ''}`}
          >
            Feedback
          </Link>
          <Link
            to="/dashboard/incident-reports"
            className={`nav-link ${isActive('/dashboard/incident-reports') ? 'active' : ''}`}
          >
            Incident Reports
          </Link>
          <Link
            to="/dashboard/reviews"
            className={`nav-link ${isActive('/dashboard/reviews') ? 'active' : ''}`}
          >
            Reviews
          </Link>
          <Link
            to="/dashboard/notifications"
            className={`nav-link ${isActive('/dashboard/notifications') ? 'active' : ''}`}
          >
            Notifications
          </Link>
          <Link
            to="/dashboard/analytics"
            className={`nav-link ${isActive('/dashboard/analytics') ? 'active' : ''}`}
          >
            Analytics
          </Link>
          <Link
            to="/dashboard/reports"
            className={`nav-link ${isActive('/dashboard/reports') ? 'active' : ''}`}
          >
            Reports
          </Link>
          <Link
            to="/dashboard/system-settings"
            className={`nav-link ${isActive('/dashboard/system-settings') ? 'active' : ''}`}
          >
            System Settings
          </Link>
        </nav>

        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="admin-name">{admin?.name || admin?.email}</div>
            <div className="admin-role">{admin?.role}</div>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="dashboard-header-titles">
            <p className="dashboard-header-brand">
              <span className="dashboard-header-brand-name">Gramio</span>
              <span className="dashboard-header-brand-sub">Admin</span>
            </p>
            <h1>{pageTitle()}</h1>
          </div>
        </header>
        <div className="dashboard-content">{children}</div>
      </main>
    </div>
  );
};

